
import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface TimeSlot {
  id: string;
  startTime: Date;
  endTime: Date;
  availability?: "available" | "limited" | "popular" | "almost-full";
}

interface TimeSlotSelectorProps {
  timeSlots: TimeSlot[];
  selectedTimeSlot: TimeSlot | null;
  onSelectTimeSlot: (timeSlot: TimeSlot) => void;
  className?: string;
}

const getAvailabilityColor = (availability: TimeSlot["availability"]) => {
  switch (availability) {
    case "available":
      return "bg-green-500";
    case "limited":
      return "bg-yellow-500";
    case "popular":
      return "bg-orange-500";
    case "almost-full":
      return "bg-red-500";
    default:
      return "bg-green-500";
  }
};

const getAvailabilityText = (availability: TimeSlot["availability"]) => {
  switch (availability) {
    case "available":
      return "Fully available";
    case "limited":
      return "Limited availability";
    case "popular":
      return "Popular time slot";
    case "almost-full":
      return "Almost full";
    default:
      return "Available";
  }
};

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  timeSlots,
  selectedTimeSlot,
  onSelectTimeSlot,
  className,
}) => {
  if (timeSlots.length === 0) {
    return (
      <div className="py-6 text-center">
        <p className="text-muted-foreground">No available time slots for this date.</p>
        <p className="text-sm mt-2">Please select a different date.</p>
      </div>
    );
  }

  return (
    <div className={cn("grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2", className)}>
      {timeSlots.map((slot) => (
        <TooltipProvider key={slot.id}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={selectedTimeSlot?.id === slot.id ? "default" : "outline"}
                className={cn(
                  "h-auto py-3 relative",
                  selectedTimeSlot?.id === slot.id ? "" : "border-input"
                )}
                onClick={() => onSelectTimeSlot(slot)}
              >
                {format(slot.startTime, "h:mm a")}
                {slot.availability && (
                  <div className="absolute bottom-0 left-0 right-0 h-1.5 rounded-b-md"
                    style={{ backgroundColor: getAvailabilityColor(slot.availability).split('-')[1] }}
                  />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{getAvailabilityText(slot.availability || "available")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </div>
  );
};

export default TimeSlotSelector;
